import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Bell, Send, Loader2, Users, Crown, User as UserIcon, History, Globe, UserX } from 'lucide-react';
import { toast } from 'sonner';

const AUDIENCES = [
  { value: 'everyone', label: 'Everyone (incl. guests)', icon: Globe },
  { value: 'all', label: 'All registered customers', icon: Users },
  { value: 'members', label: 'Members only', icon: Crown },
  { value: 'non_members', label: 'Non-members', icon: UserIcon },
  { value: 'anonymous', label: 'Anonymous visitors', icon: UserX },
  { value: 'user', label: 'Specific customer', icon: UserIcon },
];

const AdminPush = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [targetUserId, setTargetUserId] = useState('');
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [blasts, setBlasts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');

  useEffect(() => {
    if (!currentUser) navigate('/login');
    else if (currentUser.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [currentUser, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([
        api.get('/api/admin/customers?limit=500'),
        api.get('/api/push/blasts'),
      ]);
      setCustomers(c.data?.customers || []);
      setBlasts(b.data || []);
    } catch (e) {
      console.error('Load error', e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (audience === 'user' && !targetUserId) {
      toast.error('Please select a customer');
      return;
    }
    if (!window.confirm(`Send notification to "${AUDIENCES.find(a => a.value === audience)?.label}"?`)) return;

    try {
      setSending(true);
      const res = await api.post('/api/push/send', {
        title: title.trim(),
        body: body.trim(),
        audience,
        user_id: audience === 'user' ? targetUserId : null,
        url: url.trim() || null,
      });
      const d = res.data;
      const failureMsg = d.failure_summary && Object.keys(d.failure_summary).length > 0
        ? Object.entries(d.failure_summary).map(([err, n]) => `${n}× ${err}`).join('; ')
        : '';
      if (d.sent > 0) {
        toast.success(
          `Sent to ${d.sent} of ${d.subscriptions_targeted} devices (${d.recipients} customers)`
        );
      } else if (d.subscriptions_targeted === 0) {
        toast.error('No subscribed customers in this audience yet');
      } else {
        toast.error(
          `0 of ${d.subscriptions_targeted} sent. Reason: ${failureMsg || 'unknown'}`,
          { duration: 8000 }
        );
      }
      setTitle(''); setBody(''); setUrl(''); setTargetUserId('');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const filteredCustomers = customerSearch
    ? customers.filter((c) => {
        const q = customerSearch.toLowerCase();
        return (c.name || '').toLowerCase().includes(q) ||
               (c.email || '').toLowerCase().includes(q) ||
               (c.phone || '').includes(q);
      })
    : customers;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-white mb-4"
          onClick={() => navigate('/admin/dashboard')}
          data-testid="back-to-admin"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bell className="w-7 h-7 text-purple-400" />
            Push Notifications
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">
            Send announcements, deals, and reminders. Customers must enable notifications first.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Compose */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Compose</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Title (max 60 chars)</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 60))}
                  placeholder="Tap In Tuesday is LIVE 🔥"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="push-title"
                />
                <div className="text-xs text-zinc-500 mt-1 text-right">{title.length}/60</div>
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1">Message (max 200 chars)</label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, 200))}
                  placeholder="Use code TAPINTUESDAY for 15% off all haircuts today only. Book before slots fill up."
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                  data-testid="push-body"
                />
                <div className="text-xs text-zinc-500 mt-1 text-right">{body.length}/200</div>
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1">Open URL on tap (optional)</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/promotions"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="push-url"
                />
                <div className="text-xs text-zinc-500 mt-1">Leave blank to open the home page</div>
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-2">Audience</label>
                <div className="grid grid-cols-2 gap-2">
                  {AUDIENCES.map((a) => {
                    const Icon = a.icon;
                    const sel = audience === a.value;
                    return (
                      <button
                        key={a.value}
                        onClick={() => setAudience(a.value)}
                        className={`p-2 rounded-lg border text-sm flex items-center gap-2 transition ${
                          sel ? 'border-purple-500 bg-purple-500/10 text-white' : 'border-zinc-700 text-zinc-300 hover:border-zinc-600'
                        }`}
                        data-testid={`audience-${a.value}`}
                      >
                        <Icon className="w-4 h-4" />
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {audience === 'user' && (
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Customer</label>
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by name, email, or phone…"
                    className="bg-zinc-800 border-zinc-700 text-white mb-2"
                    data-testid="customer-search"
                  />
                  <div className="max-h-48 overflow-y-auto border border-zinc-700 rounded-lg">
                    {filteredCustomers.slice(0, 30).map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setTargetUserId(c.id)}
                        disabled={c.is_guest}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-800 last:border-0 ${
                          targetUserId === c.id ? 'bg-purple-500/20 text-white' : 'text-zinc-300 hover:bg-zinc-800'
                        } ${c.is_guest ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`select-customer-${c.id}`}
                      >
                        <div className="font-medium">{c.name}{c.is_guest && ' (guest, no push)'}</div>
                        <div className="text-xs text-zinc-500">{c.email || c.phone}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleSend}
                disabled={sending || !title.trim() || !body.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                data-testid="send-push-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send notification
              </Button>
            </CardContent>
          </Card>

          {/* Recent blasts log */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-400" />
                  Recent sends
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
                  onClick={async () => {
                    if (!window.confirm('Scan all push subscriptions and delete the ones with broken keys?\n\nAffected users will need to re-enable notifications.')) return;
                    try {
                      const r = await api.post('/api/push/cleanup');
                      toast.success(`Removed ${r.data.removed} broken / kept ${r.data.kept} valid subscription(s).`);
                      fetchData();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Cleanup failed');
                    }
                  }}
                  data-testid="cleanup-subs-btn"
                >
                  🧹 Clean broken subs
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {blasts.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-6">No notifications sent yet</p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {blasts.map((b) => (
                    <div
                      key={b.id}
                      className="border border-zinc-800 rounded-lg p-3 bg-zinc-950"
                      data-testid={`blast-${b.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold text-white text-sm flex-1 truncate">{b.title}</div>
                        <Badge className="bg-zinc-700 text-zinc-300 text-[10px] capitalize">
                          {b.audience.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-xs text-zinc-400 mb-2 line-clamp-2">{b.body}</p>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-3 flex-wrap">
                        <span>{new Date(b.sent_at).toLocaleString()}</span>
                        <span className="text-green-400">✓ {b.sent} sent</span>
                        {b.failed > 0 && <span className="text-red-400">✗ {b.failed} failed</span>}
                      </div>
                      {b.failed > 0 && b.failure_summary && Object.keys(b.failure_summary).length > 0 && (
                        <div className="text-[10px] text-red-300/80 mt-1.5 border-t border-zinc-800 pt-1.5">
                          {Object.entries(b.failure_summary).map(([err, n]) => (
                            <div key={err}>{n}× {err}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminPush;
